import {Bindings} from "data/constants/bindings";
import type {
	IGamebarPayload,
	IGamebarResponse,
	IRankings,
	IRankingsApiProvider,
} from "data/providers/api/rankings.api.provider";
import {inject, injectable} from "inversify";
import {uniqBy} from "lodash";
import {action, makeAutoObservable, observable, runInAction} from "mobx";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";

interface IIsMore {
	isMore: boolean;
}

interface ILeagueFetchPayload extends IIsMore {
	leagueId: number;
}

export interface IRankingFilter {
	gamedayId: string;
}

const DEFAULT_RANKING_FILTER: IRankingFilter = {
	gamedayId: "0",
};

const DEFAULT_GAMEBAR: IGamebarResponse = {
	gamedayRank: null,
	overallRank: null,
	gamedayPoints: null,
	overallPoints: null,
};

export interface IRankingsStore {
	get list(): IRankings[];
	get userRank(): IRankings | null;
	get isNextPage(): boolean;
	get rankingFilter(): IRankingFilter;
	get gamebar(): IGamebarResponse | null;

	setFilter(value: string, name: string): void;
	fetchLeaderboard(): Promise<void>;
	fetchMoreLeaderboard(): Promise<void>;

	fetchLeagueRanking(leagueId: number): Promise<void>;
	fetchMoreLeagueRanking(leagueId: number): Promise<void>;
	fetchGamebar(payload: IGamebarPayload): Promise<void>;
}

@injectable()
export class RankingsStore implements IRankingsStore {
	@observable private _list: IRankings[] = [];
	@observable private _userRank: IRankings | null = null;
	@observable private _nextPage: boolean = false;
	private _page = 1;
	@observable private _filter: IRankingFilter = DEFAULT_RANKING_FILTER;
	@observable private _gamebar: IGamebarResponse | null = null;

	constructor(
		@inject(Bindings.RankingsApiProvider) private _rankingsApi: IRankingsApiProvider,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore
	) {
		makeAutoObservable(this);
	}

	get list(): IRankings[] {
		return this._list;
	}

	get userRank(): IRankings | null {
		return this._userRank;
	}

	get isNextPage() {
		return this._nextPage;
	}

	get rankingFilter() {
		return this._filter;
	}

	get gamebar() {
		return this._gamebar;
	}

	@action setFilter(value: string, name: string) {
		if (name === "gamedayId") this._filter[name] = value;
	}

	private async leaderboardFetch({isMore}: IIsMore) {
		const {
			data: {success},
		} = await this._rankingsApi.leaderboard({
			page: this._page,
			gamedayId: Number(this._filter.gamedayId),
		});

		runInAction(() => {
			this._list = isMore
				? uniqBy([...this._list, ...success.rankings], "userId")
				: success.rankings;
			this._userRank = success.user;
			this._nextPage = success.nextPage;
		});
	}

	@action public async fetchLeaderboard() {
		this._page = 1;

		await this.leaderboardFetch({isMore: false});
	}

	@action public async fetchMoreLeaderboard() {
		this._page += 1;

		await this.leaderboardFetch({isMore: true});
	}

	private async leagueRankingFetch({isMore, leagueId}: ILeagueFetchPayload) {
		const {
			data: {success},
		} = await this._rankingsApi.league_ranking({
			page: this._page,
			leagueId: leagueId,
			gamedayId: Number(this._filter.gamedayId),
		});

		runInAction(() => {
			this._list = isMore
				? uniqBy([...this._list, ...success.rankings], "userId")
				: success.rankings;
			this._userRank = success.user;
			this._nextPage = success.nextPage;
		});
	}

	@action public async fetchLeagueRanking(leagueId: number) {
		this._page = 1;

		await this.leagueRankingFetch({isMore: false, leagueId});
	}

	@action public async fetchMoreLeagueRanking(leagueId: number) {
		this._page += 1;

		await this.leagueRankingFetch({isMore: true, leagueId});
	}

	@action public async fetchGamebar(payload: IGamebarPayload) {
		const {data} = await this._rankingsApi.gamebar(payload);

		runInAction(() => {
			this._gamebar = data.success || DEFAULT_GAMEBAR;
		});
	}
}
